// Cio che la scopa PASSA al trasporto comune (`src/lib/gamehost/`): il `dominio` delle rotte, i posti ammessi, la mossa e come si compone l'azione, il corpo di un tavolo nuovo
// (col robot), le frasi di un rifiuto e il motivo di un 409. Il trasporto non conosce nessun gioco: e questo file che glielo dice (ADR-0235, modello `lib/briscola/gioco.ts`).
//
// `componiAzione` e le liste sono costanti/funzioni DI MODULO (il trasporto tiene l'ultima callback in un ref, ma la forma di modulo non costa niente).
import { restDi } from '@/lib/gamehost/rest';
import type { VociRifiuto } from '@/lib/gamehost/rifiuti';
import { ticketRoute } from '@/lib/gamehost/ticket';
import { MOTIVI } from './protocol';
import type { AzioneDiPersona, Carta, Posto } from './protocol';
import type { LivelloRobot } from './robot';

/** Il segmento di `/v1/games/<dominio>`: `limulo-app/rotte.js` di QScopa. L'UNICO posto che nomina le rotte della scopa. */
export const SCOPA_DOMINIO = 'scopa';
export const SCOPA_REST = restDi(SCOPA_DOMINIO);
export const SCOPA_TICKET_ROUTE = ticketRoute(SCOPA_DOMINIO);

/** I posti: 2 = la scopa (uno contro uno), 4 = lo scopone scientifico (coppie a posti alterni). Il game host non ne accetta altri. */
export type PostiScopa = 2 | 4;
export const POSTI_SCOPA: PostiScopa = 2;
export const POSTI_SCOPONE: PostiScopa = 4;
/** I posti che il gioco ammette (`POSTI_AMMESSI` del game host). */
export const POSTI_AMMESSI: readonly number[] = [POSTI_SCOPA, POSTI_SCOPONE];

/**
 * 🔴 La resa vale da SEDUTI anche a tavolo che ASPETTA, anche completo (kb#476): «Esci dal tavolo» e «Abbandona» dell'Attesa. E il parametro `resaInAttesa` del trasporto comune
 * (default `false`: gli altri giochi non lo passano). Senza, `table:resign` a vista `null` non parte e la Scopa torna a perdere quel comportamento.
 */
export const RESA_IN_ATTESA = true;

/** La mossa di una persona: la carta che gioca e le carte del tavolo che prende (vuoto = la scarta). Il posto lo mette il trasporto, dall'ultimo `table:state`. */
export interface MossaScopa {
  carta: Carta;
  prendi: readonly Carta[];
}

/** Le chiavi dell'azione sono ESATTAMENTE quelle di `puòAgire` (tipo, posto, carta, prendi): un'altra chiave e `AZIONE_MALFORMATA` per il game host. */
export function componiAzione(posto: Posto, da: MossaScopa): AzioneDiPersona {
  return { tipo: 'gioca', posto, carta: da.carta, prendi: [...da.prendi] };
}

/**
 * I dati di un tavolo nuovo. `livello` (il «contro i robot») esiste SOLO per un tavolo casual: per un privato il tipo lo vieta (`never`) e, se arriva lo stesso, il corpo non lo porta
 * (un invito non ha senso con i robot: nessun posto da riempire).
 */
export type DatiTavolo = { tipo: 'casual'; posti: PostiScopa; idempotencyKey: string; livello?: LivelloRobot } | { tipo: 'privato'; posti: PostiScopa; idempotencyKey: string; livello?: never };

/**
 * Il corpo di un tavolo nuovo: i campi di sempre (`tipo`, `posti`, `idempotencyKey`) e, SOLO per un casual con un livello scelto, `robot: { livello }`. Col campo `robot` chi crea
 * siede a un posto estratto dal core e OGNI altro posto e un robot del pool (scopa: uno; scopone: tre). Senza, nessun robot. Mai `classificato`: un tavolo coi robot non conta.
 * (Forma COPIATA da `creaTavoloBriscolaBody`, TMA#746 @ e823c650, con la sola differenza che qui il tipo `privato` non porta mai il campo.)
 */
export const creaTavoloBody = (a: DatiTavolo) => {
  const base = { tipo: a.tipo, posti: a.posti, idempotencyKey: a.idempotencyKey };
  return a.tipo === 'casual' && a.livello !== undefined ? { ...base, robot: { livello: a.livello } } : base;
};

/** Le frasi di un rifiuto: i motivi di una presa sbagliata (`reason`) e i codici delle regole e del core che ne hanno una loro. */
export const VOCI_RIFIUTO: VociRifiuto = {
  motivi: MOTIVI,
  codici: ['FUORI_TURNO', 'STALE_SEQ', 'FINITA', 'NON_DISTRIBUITO', 'THROTTLED'],
};

const asRecord = (v: unknown): Record<string, unknown> => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/** Perche un INGRESSO e stato rifiutato con un 409: il solo caso in cui «Entra con un codice» ha tre frasi diverse invece di una. */
export type MotivoConflitto = 'gia-seduto' | 'pieno' | 'in-volo';
const MOTIVI_CONFLITTO = new Map<string, MotivoConflitto>([
  ['already_seated', 'gia-seduto'],
  ['table_full', 'pieno'],
  ['idempotency_in_flight', 'in-volo'],
]);

/**
 * Il MOTIVO di un 409: il core della scopa li distingue (`already_seated` anche per chi siede a un ALTRO tavolo vivo, `table_full`: gamehost-core `service/routes.ts:251-252` e `:434` @ QScopa
 * ade9d5b), il BFF li appiattisce nello status e il nome sta in `details.gameReason`. ⚠️ La FORMA e quella che Limulo produce davvero, non una scritta a mano: `projectGameRefusal`
 * (`packages/app-qlash-api/backend/game-refusal.js:17-20`) rende `{ gameReason: <codice> }` leggendo `details.gamehostCode` (che il client del kit mette a `app-qscopa/gamekit/client.js:118-134`
 * e `:243` @ origin/staging b3d7ea3), e il BFF lo spedisce in `body.details` (`packages/backend/tests/qlash-game-refusal.test.ts:30-46`: status 409 e `details: { gameReason }`).
 * I nomi `already_seated` e `table_full` li aggiunge qlash-limulo#275 (`game-refusal.js:14` @ 48da5f0a, NON ancora fusa): il vocabolario di Limulo e CHIUSO (`GAME_REASONS`), e finche non c'e il
 * campo non arriva. QUALUNQUE altro 409 (o un 409 senza `gameReason`) rende `null`: la frase resta quella di sempre (`conflittoEntra`). Lo status dice «e un 409»; il motivo e un'aggiunta,
 * mai un presupposto.
 */
export function motivoConflittoOf(errore: unknown): MotivoConflitto | null {
  const e = asRecord(errore);
  if (e.status !== 409) return null;
  const motivo = asRecord(asRecord(e.data).details).gameReason;
  return typeof motivo === 'string' ? (MOTIVI_CONFLITTO.get(motivo) ?? null) : null;
}
